/**
 * Triage Data Types
 * Types for patient triage/vitals collection in Quick Attendance Wizard
 *
 * @version 2025.1.0
 */

/**
 * Triage data collected during patient attendance
 */
export interface TriageData {
  /** Systolic blood pressure in mmHg (e.g., 120) */
  bloodPressureSystolic: number | null;
  /** Diastolic blood pressure in mmHg (e.g., 80) */
  bloodPressureDiastolic: number | null;
  /** Heart rate in beats per minute (e.g., 72) */
  heartRate: number | null;
  /** Body temperature in Celsius (e.g., 36.5) */
  temperature: number | null;
  /** Weight in kilograms (e.g., 70.5) */
  weight: number | null;
  /** Height in centimeters (e.g., 175) */
  height: number | null;
  /** Oxygen saturation percentage (e.g., 98) */
  oxygenSaturation: number | null;
  /** Additional observations/notes */
  observations: string;
}

/**
 * Initial/empty triage data
 */
export const INITIAL_TRIAGE_DATA: TriageData = {
  bloodPressureSystolic: null,
  bloodPressureDiastolic: null,
  heartRate: null,
  temperature: null,
  weight: null,
  height: null,
  oxygenSaturation: null,
  observations: '',
};

/**
 * Validation ranges for triage fields
 */
export const TRIAGE_VALIDATION = {
  bloodPressureSystolic: { min: 60, max: 250, label: 'PA Sistólica' },
  bloodPressureDiastolic: { min: 40, max: 150, label: 'PA Diastólica' },
  heartRate: { min: 30, max: 220, label: 'Frequência Cardíaca' },
  temperature: { min: 32, max: 42, label: 'Temperatura' },
  weight: { min: 20, max: 300, label: 'Peso' },
  height: { min: 100, max: 250, label: 'Altura' },
  oxygenSaturation: { min: 70, max: 100, label: 'Saturação O2' },
} as const;

/**
 * Check if triage data has any values filled
 */
export function hasTriageData(data: TriageData): boolean {
  return (
    data.bloodPressureSystolic !== null ||
    data.bloodPressureDiastolic !== null ||
    data.heartRate !== null ||
    data.temperature !== null ||
    data.weight !== null ||
    data.height !== null ||
    data.oxygenSaturation !== null ||
    data.observations.trim() !== ''
  );
}

/**
 * Format blood pressure for display
 */
export function formatBloodPressure(
  systolic: number | null,
  diastolic: number | null
): string {
  if (systolic === null && diastolic === null) return '-';
  return `${systolic ?? '-'}/${diastolic ?? '-'} mmHg`;
}

/**
 * Calculate BMI from weight and height
 */
export function calculateBMI(
  weight: number | null,
  height: number | null
): number | null {
  if (weight === null || height === null || height === 0) return null;
  const heightInMeters = height / 100;
  return Math.round((weight / (heightInMeters * heightInMeters)) * 10) / 10;
}

/**
 * Get BMI classification in Portuguese
 */
export function getBMIClassification(bmi: number | null): string {
  if (bmi === null) return '-';
  if (bmi < 18.5) return 'Abaixo do peso';
  if (bmi < 25) return 'Peso normal';
  if (bmi < 30) return 'Sobrepeso';
  if (bmi < 35) return 'Obesidade grau I';
  if (bmi < 40) return 'Obesidade grau II';
  return 'Obesidade grau III';
}
